/**
 * ObjectRef — a name, marked with what kind of thing it is.
 *
 * Every place a cycle, track, session, list or item type is named, it reads
 * "TRACK · Platform bets" with the same icon and word, so a person can tell a
 * track from a session from a priority list before clicking. One word and one
 * icon per kind, no color (color means status on these pages). When the kind
 * has a page and an id is given, the name opens that exact thing.
 *
 *   <ObjectRef kind="track" id={track.id} name={track.name} />
 *   <ObjectRef kind="work_type" name="Enhancement" />
 *
 * Give it a `summary` and the name opens a card of facts about the thing in
 * place (ds/Popover): resting the pointer on it, or tabbing to it, shows the
 * card; on a touch screen the kind mark is the press that opens it. The name
 * still opens the page.
 *
 *   <ObjectRef kind="session" name={s.name} href={s.href} summary={<SessionStepsCard … />} />
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { OBJECT_KINDS, type ObjectKind } from './objectKinds';
import { Popover, PopoverToggle } from './Popover';

export interface ObjectRefProps {
  kind: ObjectKind;
  name: string;
  /** Opens the kind's own page for this id. */
  id?: string | null;
  /** Overrides the kind's page (for a loop or ball, whose page needs more than one id). */
  href?: string | null;
  /** Leave the word out where a heading or row label already says it; the icon stays. */
  hideWord?: boolean;
  /** A card of facts about this thing, shown in place (ds/Popover). Use PopoverRows for its body. */
  summary?: ReactNode;
  className?: string;
}

export function ObjectRef({ kind, name, id, href, hideWord = false, summary, className = '' }: ObjectRefProps) {
  const def = OBJECT_KINDS[kind];
  const Icon = def.icon;
  const to = href ?? (id && def.href ? def.href(id) : null);
  const mark = (
    <span className="inline-flex shrink-0 items-center gap-1 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-dim)]" title={hideWord ? def.word : undefined}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {!hideWord && <span>{def.word}</span>}
      {hideWord && <span className="sr-only">{def.word}</span>}
    </span>
  );
  const marker = summary ? <PopoverToggle label={`${def.word} summary: ${name}`}>{mark}</PopoverToggle> : mark;
  const ref = (
    <span className={`inline-flex min-w-0 items-baseline gap-1.5 ${className}`} data-object-kind={kind}>
      {marker}
      {!hideWord && <span aria-hidden className="text-[var(--text-dim)]">·</span>}
      {to ? (
        <Link to={to} className="min-w-0 text-[var(--text-pri)] underline decoration-[var(--rule-bright)] underline-offset-[3px] hover:decoration-solid">
          {name}
        </Link>
      ) : (
        <span className="min-w-0 text-[var(--text-pri)]">{name}</span>
      )}
    </span>
  );
  if (!summary) return ref;
  return (
    <Popover label={`${def.word} summary`} content={summary}>
      {ref}
    </Popover>
  );
}

/** The kind alone — the word and icon above a page title that names one of these things. */
export function ObjectKindLabel({ kind, className = '' }: { kind: ObjectKind; className?: string }) {
  const def = OBJECT_KINDS[kind];
  const Icon = def.icon;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-dim)] ${className}`} data-object-kind={kind}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {def.word}
    </span>
  );
}
